import { useState, useEffect } from 'react'
import { QASplitView } from '@/components/qa-protocol/qa-split-view'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  ClipboardCheck,
  BarChart3,
  Play,
  Pause,
  RotateCcw,
  TrendingUp,
  Mail,
  Star,
  Users
} from 'lucide-react'

// Real customer emails will be loaded dynamically
let testEmailsData: any[] = []

// Load real test emails from public directory
const loadRealTestEmails = async () => {
  try {
    const response = await fetch('/qa_test_set.json')
    const realEmails = await response.json()

    // Transform to expected format
    testEmailsData = realEmails.map((email: any) => ({
      uid: email.uid,
      subject: email.subject,
      body: email.body,
      categories: email.categories || [email.test_category],
      test_category: email.test_category,
      customer_email: email.extracted_content?.customer_email || email.customer_email || 'unknown@example.com',
      word_count: email.word_count
    }))

    console.log(`✅ Loaded ${testEmailsData.length} real customer emails for QA testing`)
    return testEmailsData
  } catch (error) {
    console.error('Error loading real test emails:', error)
    // Fallback to mock data if real emails fail to load
    return Array.from({ length: 16 }, (_, i) => ({
      uid: `test_email_${i + 1}`,
      subject: `Test E-Mail ${i + 1} - Kundenanfrage`,
      body: `Hallo Bernhard,\n\nDies ist Test E-Mail ${i + 1} für das QS Protokoll.\n\nBitte um Hilfe mit...\n\nDanke und schöne Grüße\nKunde ${i + 1}`,
      categories: [`category_${i % 4 + 1}`],
      test_category: ["installation", "technical", "pricing", "general"][i % 4],
      customer_email: `kunde${i + 1}@example.com`,
      word_count: 25 + i
    }))
  }
}

interface QASession {
  id: string
  name: string
  promptVersion: string
  progress: number
  totalTests: number
  completedTests: number
  avgRating?: number
}

export function QATestProtocol() {
  const [currentEmailIndex, setCurrentEmailIndex] = useState(0)
  const [isSessionActive, setIsSessionActive] = useState(false)
  const [isBatchProcessing, setIsBatchProcessing] = useState(false)
  const [batchProgress, setBatchProgress] = useState(0)
  const [aiResponses, setAiResponses] = useState<Record<string, string>>({})
  const [testResults, setTestResults] = useState<Record<string, any>>({})
  const [currentReviewer, setCurrentReviewer] = useState<string>(() => {
    // Load saved reviewer from localStorage
    return localStorage.getItem('qa_selected_reviewer') || 'Bernhard'
  })
  const [realTestEmails, setRealTestEmails] = useState<any[]>([])
  const [isLoadingEmails, setIsLoadingEmails] = useState(true)
  const [hasCachedBatch, setHasCachedBatch] = useState(false)
  const [allRated, setAllRated] = useState(false)
  const [currentUserRated, setCurrentUserRated] = useState(false)
  const [sessionStats, setSessionStats] = useState<{
    totalEmails: number
    ratedByCurrentUser: number
    ratedByAllUsers: number
    averageRating: number
    sessionId: string
  } | null>(null)
  const [currentSession, setCurrentSession] = useState<QASession>({
    id: `session_${Date.now()}`,
    name: 'Prompt v1.2 Testing',
    promptVersion: '1.2.0',
    progress: 0,
    totalTests: 0,
    completedTests: 0
  })

  // LocalStorage keys for caching (backup)
  const BATCH_CACHE_KEY = 'qa_batch_responses'
  const RATINGS_CACHE_KEY = 'qa_batch_ratings'
  const SESSION_CACHE_KEY = 'qa_current_session'

  // Load real test emails and check for cached batch on component mount
  useEffect(() => {
    const loadEmails = async () => {
      setIsLoadingEmails(true)
      const emails = await loadRealTestEmails()
      setRealTestEmails(emails)

      // Update session with actual email count
      setCurrentSession(prev => ({
        ...prev,
        totalTests: emails.length
      }))

      // SLC KISS: Only use Supabase as single source of truth
      await checkForActiveSupabaseSession(emails)

      // Clear conflicting localStorage cache
      localStorage.removeItem(BATCH_CACHE_KEY)
      localStorage.removeItem(RATINGS_CACHE_KEY)
      localStorage.removeItem(SESSION_CACHE_KEY)

      console.log(`🧹 Cleared localStorage cache - using Supabase only`)

      setIsLoadingEmails(false)
    }
    loadEmails()
  }, [])

  // Check for active team session in Supabase with smart batch detection
  const checkForActiveSupabaseSession = async (emails: any[]) => {
    try {
      const { supabase } = await import('@/lib/supabase')

      // Get recent QA entries (last 24 hours) and detect batches
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
      const { data: recentEntries, error } = await supabase
        .from('email_qa_reviews')
        .select('*')
        .gte('created_at', oneDayAgo)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error checking Supabase session:', error)
        return
      }

      if (recentEntries && recentEntries.length > 0) {
        console.log(`🌐 Found ${recentEntries.length} recent QA entries`)

        // Detect latest batch using timestamp clustering
        const latestEntry = recentEntries[0]
        const latestTime = new Date(latestEntry.created_at).getTime()
        const batchWindow = 5 * 60 * 1000 // 5 minutes window for batch detection

        // Group entries within batch window of latest entry
        const latestBatch = recentEntries.filter(entry => {
          const entryTime = new Date(entry.created_at).getTime()
          return (latestTime - entryTime) <= batchWindow
        })

        console.log(`🔍 Batch detection: ${latestBatch.length} emails in latest batch`)
        console.log(`⏰ Batch timespan: ${latestBatch.length > 0 ?
          `${new Date(latestBatch[latestBatch.length - 1].created_at).toLocaleTimeString()} - ${new Date(latestBatch[0].created_at).toLocaleTimeString()}` :
          'N/A'
        }`)

        if (latestBatch.length >= 5) { // Accept any reasonable batch size
          // KISS: Use database entries directly as test cases!
          console.log(`✅ KISS Solution: Using ${latestBatch.length} database entries directly`)

          // Transform database entries to QA test format
          const transformedEmails = latestBatch.map((entry, index) => ({
            uid: entry.session_id || `qa_entry_${index}`,
            subject: entry.email_subject || `QA Test ${index + 1}`,
            body: (entry.original_email || 'No content')
              .replace(/\*\*(.*?)\*\*/g, '$1')           // Remove markdown bold
              .replace(/### (.*)/g, '$1:')               // Convert h3 to simple text
              .replace(/## (.*)/g, '$1:')                // Convert h2 to simple text
              .replace(/- /g, '• ')                      // Convert markdown lists
              .replace(/\n\n/g, '\n')                    // Reduce double newlines
              .trim(),
            customer_email: entry.session_id,
            test_category: entry.email_category || 'general',
            word_count: entry.original_email?.split(' ').length || 0,
            ai_response: entry.final_response || entry.ai_response
          }))

          // Create responses object with UID as key
          const batchResponses: Record<string, string> = {}
          transformedEmails.forEach(email => {
            if (email.ai_response) {
              batchResponses[email.uid] = email.ai_response
            }
          })

          // Update email list to use actual database entries
          setRealTestEmails(transformedEmails)
          setAiResponses(batchResponses)
          setHasCachedBatch(true)

          console.log(`🎯 KISS: ${transformedEmails.length} database entries ready for QA`)

          // Analyze ratings
          const allRatedEntries = latestBatch.filter(entry => entry.rating !== null)
          const currentUserRatedEntries = latestBatch.filter(entry =>
            entry.rating !== null && entry.reviewer === currentReviewer
          )

          const allTeamRated = allRatedEntries.length >= emails.length
          const currentUserComplete = currentUserRatedEntries.length >= emails.length
          const avgRating = allRatedEntries.length > 0
            ? allRatedEntries.reduce((sum, entry) => sum + (entry.rating || 0), 0) / allRatedEntries.length
            : 0

          setAllRated(allTeamRated)
          setCurrentUserRated(currentUserComplete)

          // Create batch statistics
          const batchId = `batch_${new Date(latestEntry.created_at).getTime()}`
          setSessionStats({
            totalEmails: latestBatch.length,
            ratedByCurrentUser: currentUserRatedEntries.length,
            ratedByAllUsers: allRatedEntries.length,
            averageRating: Math.round(avgRating * 10) / 10,
            sessionId: batchId
          })

          console.log(`🎯 LATEST BATCH DETECTED:`)
          console.log(`  📊 Database entries: ${latestBatch.length}`)
          console.log(`  📊 Successful mappings: ${Object.keys(batchResponses).length}/${emails.length}`)
          console.log(`  📊 Missing mappings: ${latestBatch.length - Object.keys(batchResponses).length}`)
          console.log(`  👤 ${currentReviewer}: ${currentUserRatedEntries.length}/${emails.length} rated`)
          console.log(`  👥 Team total: ${allRatedEntries.length} ratings`)
          console.log(`  ⭐ Team average: ${avgRating.toFixed(1)}/5`)
          console.log(`  📅 Batch ID: ${batchId}`)

          // Update session info
          setCurrentSession(prev => ({
            ...prev,
            id: batchId,
            name: `Latest Batch (${new Date(latestEntry.created_at).toLocaleString('de-DE')})`,
            totalTests: emails.length,
            completedTests: currentUserRatedEntries.length,
            avgRating: currentUserRatedEntries.length > 0
              ? currentUserRatedEntries.reduce((sum, entry) => sum + (entry.rating || 0), 0) / currentUserRatedEntries.length
              : undefined
          }))

        } else {
          console.log(`⚠️ Incomplete batch detected: only ${latestBatch.length}/${emails.length} entries`)
          setSessionStats(null)
        }
      } else {
        console.log(`🌐 No recent QA entries found - ready for new batch`)
        setSessionStats(null)
      }
    } catch (error) {
      console.error('Error checking Supabase session:', error)
    }
  }

  const handleRating = async (emailUid: string, rating: number, notes: string) => {
    // Save to database with current reviewer
    try {
      const response = await fetch('/api/qa-save-rating', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          test_session_id: currentSession.id,
          source_uid: emailUid,
          reviewer: currentReviewer,
          rating,
          notes: notes || null
        }),
      })

      if (!response.ok) {
        throw new Error(`Failed to save rating: ${response.status}`)
      }

      console.log(`✅ Rating saved: ${currentReviewer} rated ${emailUid} with ${rating} stars`)
    } catch (error) {
      console.error('Error saving rating:', error)
    }

    // Update local state
    const reviewerKey = `${emailUid}_${currentReviewer}`
    setTestResults(prev => ({
      ...prev,
      [reviewerKey]: {
        rating,
        notes,
        reviewer: currentReviewer,
        timestamp: new Date().toISOString()
      }
    }))

    // Update session progress (count unique emails rated by current reviewer)
    const currentReviewerRatings = Object.keys(testResults)
      .filter(key => key.endsWith(`_${currentReviewer}`))
      .length + 1

    // SLC KISS: Ratings saved to Supabase via API (no localStorage)

    // Check if all emails are rated (by any reviewer)
    const uniqueEmailIds = new Set()
    Object.keys(testResults).forEach(key => {
      const emailId = key.split('_').slice(0, -1).join('_')
      uniqueEmailIds.add(emailId)
    })
    const allEmailsRated = uniqueEmailIds.size >= realTestEmails.length
    setAllRated(allEmailsRated)

    if (allEmailsRated) {
      console.log(`🎉 All ${realTestEmails.length} emails have been rated! New batch can be generated.`)
    }

    setCurrentSession(prev => {
      const updatedSession = {
        ...prev,
        completedTests: currentReviewerRatings,
        progress: (currentReviewerRatings / prev.totalTests) * 100,
        avgRating: calculateAvgRating(testResults, currentReviewer)
      }

      // SLC KISS: Session managed via Supabase only
      return updatedSession
    })
  }

  const calculateAvgRating = (results: Record<string, any>, reviewer?: string) => {
    if (!reviewer) {
      const ratings = Object.values(results).map((r: any) => r.rating).filter(Boolean)
      return ratings.length > 0 ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length : 0
    }

    // Calculate average for specific reviewer
    const reviewerRatings = Object.values(results)
      .filter((r: any) => r.reviewer === reviewer)
      .map((r: any) => r.rating)
      .filter(Boolean)

    return reviewerRatings.length > 0
      ? reviewerRatings.reduce((sum, rating) => sum + rating, 0) / reviewerRatings.length
      : 0
  }

  const handleNext = () => {
    if (currentEmailIndex < testEmailsData.length - 1) {
      setCurrentEmailIndex(currentEmailIndex + 1)
    }
  }

  const handlePrevious = () => {
    if (currentEmailIndex > 0) {
      setCurrentEmailIndex(currentEmailIndex - 1)
    }
  }

  const startBatchProcessing = async () => {
    setIsBatchProcessing(true)
    setBatchProgress(0)
    setAiResponses({})

    try {
      const sessionId = `qa_batch_${Date.now()}`

      // Process all emails in batch with enhanced API
      for (let i = 0; i < realTestEmails.length; i++) {
        const email = realTestEmails[i]

        setBatchProgress((i / realTestEmails.length) * 100)

        const request = {
          email_address: email.customer_email || 'test@example.com',
          email_name: email.customer_email?.split('@')[0] || 'Kunde',
          email_subject: email.subject,
          email_content: email.body,
          timestamp: new Date().toISOString(),
          qa_session_id: sessionId,
          qa_test_uid: email.uid
        }

        try {
          // Use existing EmailDrafter service - much simpler!
          const { emailDrafterService } = await import('@/services/email-drafter')
          const response = await emailDrafterService.sendEmailDraft(request)

          setAiResponses(prev => ({
            ...prev,
            [email.uid]: response
          }))

          console.log(`✅ Email ${i + 1}/16 processed:`, {
            uid: email.uid,
            subject: email.subject
          })

        } catch (error) {
          console.error(`❌ Error processing email ${i + 1}:`, error)
          setAiResponses(prev => ({
            ...prev,
            [email.uid]: `Fehler: ${error instanceof Error ? error.message : 'Unbekannt'}`
          }))
        }

        // Delay to prevent rate limiting and allow webhook processing
        await new Promise(resolve => setTimeout(resolve, 1000))
      }

      setBatchProgress(100)

      // SLC KISS: Save only to Supabase (single source of truth)
      await saveToSupabaseSession(sessionId, realTestEmails, aiResponses)

      setHasCachedBatch(true)
      setIsSessionActive(true)
      setCurrentEmailIndex(0)

      setCurrentSession(prev => ({
        ...prev,
        id: sessionId,
        name: `Team Session (${sessionId.slice(-8)})`,
        totalTests: realTestEmails.length
      }))

      console.log(`🎉 Batch processing complete! Session: ${sessionId}`)
      console.log(`💾 Saved to BOTH Supabase (team) AND localStorage (backup)`)
    } catch (error) {
      console.error('Batch processing error:', error)
    } finally {
      setIsBatchProcessing(false)
    }
  }

  const pauseSession = () => {
    setIsSessionActive(false)
  }

  const resetSession = async () => {
    // SLC KISS: Simple Supabase-only reset
    try {
      const { supabase } = await import('@/lib/supabase')
      await supabase
        .from('email_qa_reviews')
        .update({ status: 'archived' })
        .eq('status', 'pending')

      console.log(`🗑️ SLC KISS: Archived pending sessions, ready for new batch`)
    } catch (error) {
      console.error('Error archiving session:', error)
    }

    // Reset all state
    setIsSessionActive(false)
    setIsBatchProcessing(false)
    setCurrentEmailIndex(0)
    setTestResults({})
    setAiResponses({})
    setBatchProgress(0)
    setHasCachedBatch(false)
    setAllRated(false)
    setCurrentUserRated(false)
    setSessionStats(null)

    setCurrentSession(prev => ({
      ...prev,
      id: `session_${Date.now()}`,
      name: 'Prompt v1.2 Testing',
      completedTests: 0,
      progress: 0,
      avgRating: undefined
    }))
  }

  // Save generated emails to Supabase for team access
  const saveToSupabaseSession = async (sessionId: string, emails: any[], responses: Record<string, string>) => {
    try {
      const { supabase } = await import('@/lib/supabase')

      console.log(`🌐 Saving team session to Supabase: ${sessionId}`)

      // Create email_qa_reviews entries for each generated response
      const qaEntries = emails.map(email => ({
        test_session_id: sessionId,
        source_uid: email.uid,
        original_email: email.body,
        email_subject: email.subject,
        customer_email: email.customer_email,
        ai_response: responses[email.uid],
        email_category: email.test_category,
        complexity_level: email.complexity_level || 'medium',
        expected_refiner: email.expected_refiner || 'mission_solar',
        urgency_level: email.urgency_level || 'normal',
        test_status: 'pending'
      }))

      const { error } = await supabase
        .from('email_qa_reviews')
        .insert(qaEntries)

      if (error) {
        console.error('❌ Error saving to Supabase:', error)
      } else {
        console.log(`✅ Saved ${qaEntries.length} QA entries to Supabase for team access`)
      }
    } catch (error) {
      console.error('Error saving Supabase session:', error)
    }
  }

  const clearCacheAndGenerateNew = () => {
    resetSession()
    // Small delay to ensure state is reset, then start new batch
    setTimeout(() => {
      startBatchProcessing()
    }, 100)
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <ClipboardCheck className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0" />
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">
                  QS Test Protokoll
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground">
                  Systematische QA mit echten Kunden-E-Mails für AI Response Optimierung
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              {/* Reviewer Selection moved to top-right */}
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Reviewer:</span>
                <Select
                  value={currentReviewer}
                  onValueChange={(value) => {
                    setCurrentReviewer(value)
                    localStorage.setItem('qa_selected_reviewer', value)
                    console.log(`👤 Reviewer saved: ${value}`)
                  }}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bernhard">Bernhard</SelectItem>
                    <SelectItem value="Thomas">Thomas</SelectItem>
                    <SelectItem value="Christoph">Christoph</SelectItem>
                    <SelectItem value="Stefan">Stefan</SelectItem>
                    <SelectItem value="Mathias">Mathias</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Session controls when active */}
              {isSessionActive && (
                <>
                  <Button variant="outline" size="sm" onClick={pauseSession}>
                    <Pause className="mr-1 h-4 w-4" />
                    Pause
                  </Button>
                  <Button variant="outline" size="sm" onClick={resetSession}>
                    <RotateCcw className="mr-1 h-4 w-4" />
                    Reset
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Session Overview */}
          <Card className="mb-6 border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Session: {currentSession.name}
                </CardTitle>
                {/* Reviewer shown in session overview, no duplicate */}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div className="space-y-1">
                  <div className="text-muted-foreground">Tests</div>
                  <div className="font-semibold">
                    {currentSession.completedTests} / {currentSession.totalTests}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-muted-foreground">Fortschritt</div>
                  <div className="font-semibold">
                    {Math.round(currentSession.progress)}%
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-muted-foreground">Ø Bewertung</div>
                  <div className="font-semibold flex items-center gap-1">
                    {currentSession.avgRating ? (
                      <>
                        <Star className="h-4 w-4 text-yellow-500 fill-current" />
                        {currentSession.avgRating.toFixed(1)}
                      </>
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-muted-foreground">Reviewer</div>
                  <Badge variant="outline">{currentReviewer}</Badge>
                </div>
                <div className="space-y-1">
                  <div className="text-muted-foreground">Prompt Version</div>
                  <Badge variant="outline">{currentSession.promptVersion}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Batch Processing Progress */}
        {isBatchProcessing && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 animate-spin" />
                Verarbeitung läuft...
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                    style={{ width: `${batchProgress}%` }}
                  />
                </div>
                <p className="text-center text-sm text-muted-foreground">
                  {Math.round(batchProgress)}% abgeschlossen -
                  Verarbeitung von {testEmailsData.length} E-Mails mit vollständiger Agent Pipeline
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Testing Interface */}
        {isLoadingEmails ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 rounded-full mx-auto mb-4"></div>
              <p>Lade echte Kunden-E-Mails...</p>
            </CardContent>
          </Card>
        ) : isSessionActive ? (
          <QASplitView
            testEmails={realTestEmails}
            currentIndex={currentEmailIndex}
            onRating={handleRating}
            onNext={handleNext}
            onPrevious={handlePrevious}
            preGeneratedResponses={aiResponses}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                QA Test Overview
              </CardTitle>
              <CardDescription>
                {realTestEmails.length} echte Kunden-E-Mails bereit für systematisches Testing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 border rounded-lg">
                    <div className="text-2xl font-bold">{realTestEmails.length}</div>
                    <div className="text-sm text-muted-foreground">Test E-Mails</div>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <div className="text-2xl font-bold">7+</div>
                    <div className="text-sm text-muted-foreground">Kategorien</div>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <div className="text-2xl font-bold">843</div>
                    <div className="text-sm text-muted-foreground">Gesamt Basis</div>
                  </div>
                    <div className="text-center p-4 border rounded-lg">
                    <div className="text-2xl font-bold">1-5</div>
                    <div className="text-sm text-muted-foreground">Sterne Rating</div>
                  </div>
                </div>

                {/* Cached Batch Status */}
                {hasCachedBatch && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-blue-800">
                        Cached Batch Status
                      </span>
                    </div>
                    <div className="text-sm text-blue-700 space-y-2">
                      {/* Generation Status */}
                      {Object.keys(aiResponses).length >= realTestEmails.length ? (
                        <p>✅ Alle {Object.keys(aiResponses).length} AI Antworten generiert!</p>
                      ) : (
                        <p>❌ Nur {Object.keys(aiResponses).length} von {realTestEmails.length} E-Mails generiert - Batch incomplete!</p>
                      )}

                      {/* Current User Status */}
                      {sessionStats && (
                        <div className="space-y-1">
                          {currentUserRated ? (
                            <p className="font-medium text-green-700">
                              👤 Sie haben alle {sessionStats.ratedByCurrentUser} E-Mails bewertet! ✅
                            </p>
                          ) : (
                            <p>
                              👤 Sie: {sessionStats.ratedByCurrentUser}/{sessionStats.totalEmails} bewertet
                            </p>
                          )}

                          <p>
                            👥 Team gesamt: {sessionStats.ratedByAllUsers} Bewertungen (Ø {sessionStats.averageRating}/5 ⭐)
                          </p>

                          <p className="text-xs">
                            🎯 Session: {sessionStats.sessionId.slice(-12)}
                          </p>
                        </div>
                      )}

                      <div className="mt-2 text-xs">
                        {Object.keys(aiResponses).length >= realTestEmails.length ?
                          currentUserRated ?
                            "✅ Sie können nochmals bewerten oder neue Session starten" :
                            "🌐 Team-Session bereit - Sie können jetzt bewerten" :
                          "⚠️ AI Antworten müssen erst vollständig generiert werden"
                        }
                      </div>
                    </div>
                  </div>
                )}

                <div className="text-center space-y-4">
                  <p className="text-muted-foreground">
                    {!hasCachedBatch ?
                      `Starten Sie Team-Batch-Verarbeitung aller ${realTestEmails.length} E-Mails für kollaborative Bewertung` :
                      allRated ?
                        `Alle E-Mails vom Team bewertet! Neue Team-Batch kann generiert werden.` :
                        `Weiter mit Team-Bewertung der generierten E-Mails`
                    }
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {!hasCachedBatch ? (
                      <>
                        <Button onClick={startBatchProcessing} size="lg" variant="default">
                          <Play className="mr-2 h-4 w-4" />
                          Alle {realTestEmails.length} E-Mails verarbeiten
                        </Button>
                        <Button
                          onClick={() => {
                            setIsSessionActive(true)
                            setCurrentEmailIndex(0)
                          }}
                          size="lg"
                          variant="outline"
                        >
                          <Mail className="mr-2 h-4 w-4" />
                          Schrittweiser Modus
                        </Button>
                      </>
                    ) : allRated ? (
                      <Button onClick={clearCacheAndGenerateNew} size="lg" variant="default">
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Neue Team-Batch ({realTestEmails.length} E-Mails)
                      </Button>
                    ) : Object.keys(aiResponses).length >= realTestEmails.length ? (
                      currentUserRated ? (
                        <>
                          <Button onClick={() => setIsSessionActive(true)} size="lg" variant="outline">
                            <Star className="mr-2 h-4 w-4" />
                            Session nochmals bewerten
                          </Button>
                          <Button onClick={clearCacheAndGenerateNew} size="lg" variant="default">
                            <RotateCcw className="mr-2 h-4 w-4" />
                            Neue Team-Session starten
                          </Button>
                        </>
                      ) : (
                        <Button onClick={() => setIsSessionActive(true)} size="lg" variant="default">
                          <Play className="mr-2 h-4 w-4" />
                          Team-Session bewerten
                        </Button>
                      )
                    ) : (
                      <Button onClick={startBatchProcessing} size="lg" variant="outline">
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Fehlende AI Antworten generieren
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {!hasCachedBatch ?
                      "Team-Batch: Alle sehen gleiche E-Mails • Schrittweise: Individueller Modus" :
                      "🌐 Team-Session: Alle Benutzer können diese E-Mails bewerten bis vollständig"
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}